import { z } from "zod";

/** Stripe Price IDs always start with `price_`. Product IDs (`prod_`) and
 *  similar look-alikes are rejected here so the user gets a clear error at
 *  startup instead of a confusing "No such price" from the Stripe API. */
function stripePriceId() {
  return z.string().refine(
    (v) => v.startsWith("price_"),
    (v) => ({
      message:
        v.startsWith("prod_")
          ? `Got a Stripe Product ID (\`${v}\`) where a Price ID is required. Open the Product in the Stripe Dashboard, scroll to "Pricing", and copy the \`price_…\` ID.`
          : `Stripe Price ID must start with \`price_\` (got \`${v}\`).`,
    }),
  );
}

const PublicSchema = z.object({
  NEXT_PUBLIC_APP_URL: z.string().url().default("http://localhost:3001"),
  NEXT_PUBLIC_SITE_URL: z.string().url().default("http://localhost:3000"),
  NEXT_PUBLIC_SIM_URL: z.string().url().default("http://localhost:3002"),
  NEXT_PUBLIC_TRAINER_URL: z.string().url().default("http://localhost:3003"),
  /**
   * PostHog project key + host. Both optional so dev runs without an
   * analytics account; when set, the provider boots posthog-js
   * client-side. The host defaults to PostHog Cloud US.
   */
  NEXT_PUBLIC_POSTHOG_KEY: z.string().optional(),
  NEXT_PUBLIC_POSTHOG_HOST: z.string().url().default("https://us.i.posthog.com"),
});

const ServerSchema = PublicSchema.extend({
  // Stripe — required for any checkout/portal/webhook traffic.
  STRIPE_SECRET_KEY: z.string().optional(),
  STRIPE_WEBHOOK_SECRET: z.string().optional(),

  // Per-tier Stripe Price IDs. Optional individually so dev can run without
  // the full set, but the relevant one is required when checkout is hit.
  // The `price_` prefix check catches the most common configuration mistake:
  // pasting a Product ID (`prod_…`) where a Price ID is required. Stripe
  // Checkout's error in that case is "No such price: 'prod_…'", which only
  // makes sense if you already know the difference.
  STRIPE_PRICE_TRAINEE_MONTHLY: stripePriceId().optional(),
  STRIPE_PRICE_TRAINEE_YEARLY: stripePriceId().optional(),
  STRIPE_PRICE_PRO_MONTHLY: stripePriceId().optional(),
  STRIPE_PRICE_PRO_YEARLY: stripePriceId().optional(),

  // Email + analytics — all optional.
  RESEND_API_KEY: z.string().optional(),
  // RFC 5322 mailbox: either a bare address or `Name <addr@host>`. We don't
  // use `.email()` because that rejects the display-name form, which is
  // both legal in SMTP From headers and what we send by default.
  WAITLIST_FROM_EMAIL: z.string().min(1).default("ModernATC <hello@modernatc.com>"),
});

export const publicEnv = PublicSchema.parse({
  NEXT_PUBLIC_APP_URL: process.env.NEXT_PUBLIC_APP_URL,
  NEXT_PUBLIC_SITE_URL: process.env.NEXT_PUBLIC_SITE_URL,
  NEXT_PUBLIC_SIM_URL: process.env.NEXT_PUBLIC_SIM_URL,
  NEXT_PUBLIC_TRAINER_URL: process.env.NEXT_PUBLIC_TRAINER_URL,
  NEXT_PUBLIC_POSTHOG_KEY: process.env.NEXT_PUBLIC_POSTHOG_KEY,
  NEXT_PUBLIC_POSTHOG_HOST: process.env.NEXT_PUBLIC_POSTHOG_HOST,
});

export function readServerEnv() {
  return ServerSchema.parse({
    NEXT_PUBLIC_APP_URL: process.env.NEXT_PUBLIC_APP_URL,
    NEXT_PUBLIC_SITE_URL: process.env.NEXT_PUBLIC_SITE_URL,
    STRIPE_SECRET_KEY: process.env.STRIPE_SECRET_KEY,
    STRIPE_WEBHOOK_SECRET: process.env.STRIPE_WEBHOOK_SECRET,
    STRIPE_PRICE_TRAINEE_MONTHLY: process.env.STRIPE_PRICE_TRAINEE_MONTHLY,
    STRIPE_PRICE_TRAINEE_YEARLY: process.env.STRIPE_PRICE_TRAINEE_YEARLY,
    STRIPE_PRICE_PRO_MONTHLY: process.env.STRIPE_PRICE_PRO_MONTHLY,
    STRIPE_PRICE_PRO_YEARLY: process.env.STRIPE_PRICE_PRO_YEARLY,
    RESEND_API_KEY: process.env.RESEND_API_KEY,
    WAITLIST_FROM_EMAIL: process.env.WAITLIST_FROM_EMAIL,
    NEXT_PUBLIC_SIM_URL: process.env.NEXT_PUBLIC_SIM_URL,
  });
}
